import { Box, Stack, Text } from '@mantine/core';
import { useSearchFiltersContext } from 'context/SearchFilters.context';
import { Actions, trackAction } from 'utils/amplitude';
import { useMediaQuery } from '@mantine/hooks';
import { handleSelection } from 'utils/filters/filters';
import { PebbleCheckbox } from 'components/ui';
import classes from './SearchFilterOptions.module.scss';
import classNames from 'classnames';

const SearchFilterOptions: React.FC = () => {
  const {
    setCurrentInnerModalComponent,
    getCategoryLabel,
    getProvidersLabel,
    onFilterChange,
    onlineFilterIncluded,
    instantBookFilterIncluded,
    selectedFilters,
    selectedProviders,
    getTimeLabel,
  } = useSearchFiltersContext();

  const isMobile = useMediaQuery('(max-width: 768px)', true);

  const handleTrackEvent = (checkboxFlag: boolean, isOnline?: boolean) => {
    if (isOnline) {
      return checkboxFlag
        ? trackAction(Actions.ONLINE_INCLUDE)
        : trackAction(Actions.ONLINE_EXCLUDE);
    } else {
      return checkboxFlag
        ? trackAction(Actions.INSTANT_BOOK_FILTER_INCLUDE)
        : trackAction(Actions.INSTANT_BOOK_FILTER_EXCLUDE);
    }
  };

  const hasNoTimeFilters = !selectedFilters.time;
  const timeLabel = hasNoTimeFilters ? 'Any time' : 'update';
  const hasSelectedProviders = selectedProviders.length > 0;

  return (
    <Stack w={isMobile ? '100%' : 350} justify="space-evenly">
      <Box
        mt="md"
        className={classNames(classes.card, {
          [classes.additionalPadding]: hasNoTimeFilters,
          [classes.cardWithFiltersApplied]: !hasNoTimeFilters,
        })}
        onClick={() => {
          trackAction(Actions.TIME_FILTER_SELECT);
          setCurrentInnerModalComponent('time');
        }}
        role="menuitem"
      >
        <Stack gap={0}>
          <Text className={classes.cardLeftSection}>Time</Text>
          <Text className={classes.scheduleFilterValue} pt={hasNoTimeFilters ? 0 : 'sm'}>
            {hasNoTimeFilters ? '' : getTimeLabel()}
          </Text>
        </Stack>
        <Text
          className={classNames(classes.cardRightSection, {
            [classes.update]: !hasNoTimeFilters,
          })}
        >
          {timeLabel}
        </Text>
      </Box>
      <Box
        className={classNames(classes.card, {
          [classes.additionalPadding]: !hasSelectedProviders,
          [classes.cardWithFiltersApplied]: hasSelectedProviders,
        })}
        onClick={() => {
          setCurrentInnerModalComponent('providers');
          trackAction(Actions.PROVIDER_FILTER_SELECT);
        }}
        role="menuitem"
      >
        <Stack gap={0}>
          <Text className={classes.cardLeftSection}>Provider</Text>
          <Text className={classes.scheduleFilterValue} pt={hasSelectedProviders ? 'sm' : 0}>
            {hasSelectedProviders ? getProvidersLabel() : ''}
          </Text>
        </Stack>
        <Text
          className={classNames(classes.cardRightSection, {
            [classes.update]: hasSelectedProviders,
          })}
        >
          {hasSelectedProviders ? 'update' : 'All providers'}
        </Text>
      </Box>
      <Box
        className={classNames(classes.card, {
          [classes.additionalPadding]: !getCategoryLabel(),
          [classes.cardWithFiltersApplied]: getCategoryLabel(),
        })}
        onClick={() => {
          trackAction(Actions.CATEGORY_FILTER_SELECT);
          setCurrentInnerModalComponent('categories');
        }}
        role="menuitem"
      >
        <Stack gap={0}>
          <Text className={classes.cardLeftSection}>Category</Text>
          <Text className={classes.scheduleFilterValue} pt={getCategoryLabel() ? 'sm' : 0}>
            {getCategoryLabel()}
          </Text>
        </Stack>
        <Text
          className={classNames(classes.cardRightSection, { [classes.update]: getCategoryLabel() })}
        >
          {getCategoryLabel() ? 'Update' : 'All categories'}
        </Text>
      </Box>
      <Box className={classNames(classes.card, classes.additionalPadding)} role="menuitem">
        <PebbleCheckbox
          label="Include online"
          labelPosition="left"
          fullWidth
          onChange={(event) => {
            handleSelection(event.currentTarget.checked ? 'true' : '', 'isOnline', onFilterChange);
            handleTrackEvent(event.currentTarget.checked, true);
          }}
          checked={onlineFilterIncluded}
          classNames={{
            label: classes.cardLeftSection,
            root: classes.checkboxBody,
          }}
        />
      </Box>
      <Box className={classNames(classes.card, classes.additionalPadding)} role="menuitem">
        <PebbleCheckbox
          label="Show instant book only"
          labelPosition="left"
          fullWidth
          onChange={(event) => {
            handleSelection(
              event.currentTarget.checked ? 'true' : '',
              'isInstantBookOnly',
              onFilterChange,
            );
            handleTrackEvent(event.currentTarget.checked);
          }}
          checked={instantBookFilterIncluded}
          classNames={{
            label: classes.cardLeftSection,
            root: classes.checkboxBody,
          }}
        />
      </Box>
    </Stack>
  );
};

export default SearchFilterOptions;
